import { useEffect, useState } from "react"
import { useQuery } from "react-query"

import { isSubmittable } from "./launchAddOnPricing"

const DEBOUNCE_MS = 300

// The gallons field fires on every keystroke and each one would otherwise be a
// round trip, so the selection is allowed to settle before it is priced.
const useSettled = (value, delay = DEBOUNCE_MS) => {
  const [settled, setSettled] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])

  return settled
}

// Subtotal, Tax, and Estimated total for a selection nobody has submitted yet.
//
// Every number and every word comes back from the server: the client holds no
// tax rates, and the marina's tax configuration decides whether fuel is taxed at
// all. Doing this arithmetic here would quote a boater something the marina is
// not about to charge.
export const useLaunchAddOnEstimate = ({
  catalog,
  value,
  enabled,
  scopeId,
  fetchEstimate,
}) => {
  // The same gate the submit payload uses, so the block prices exactly what the
  // boater can actually order. The three cases it drops fail three different
  // ways and all of them badly: an unpriced product trips LaunchAddon's
  // numericality validation and 400s the entire quote, an out-of-stock one has
  // no such validation and is priced into a total nothing will honour, and a
  // half-specified fuel line is rejected outright. The rest of the cart still
  // gets a real subtotal and a real tax row.
  const priceable = value.filter((selection) =>
    isSubmittable(
      catalog.find((item) => item.productId === selection.productId),
      selection
    )
  )

  const settled = useSettled(JSON.stringify(priceable))

  const { data, isError } = useQuery(
    // Keyed by whose price list this is as well as by what was picked. Product
    // ids are UUIDs, so two marinas cannot collide on selection alone today —
    // but what comes back is the marina's tax configuration applied to that
    // selection, and none of that is in the selection itself.
    ["launchAddOnEstimate", scopeId, settled],
    () => fetchEstimate(JSON.parse(settled)),
    {
      enabled: Boolean(enabled) && JSON.parse(settled).length > 0,
      refetchOnWindowFocus: false,
      retry: false,
      // The previous rows stay on screen while the next quote is in flight, so
      // adding an item does not blank the block the boater is reading.
      keepPreviousData: true,
    }
  )

  const pricing = priceable.length > 0

  return {
    estimate: pricing ? data?.estimate ?? null : null,
    // A failed quote otherwise renders identically to nothing-priceable-selected.
    // keepPreviousData means the last total is still sitting there, so without
    // this the block goes quietly stale rather than empty.
    failed: pricing && isError,
  }
}
